import React, { useState } from 'react';
import { Select } from '@amzn/stencil-react-components/select';
import { Input, InputWrapper } from '@amzn/stencil-react-components/input';
import { Col, Row, Spacer, View } from '@amzn/stencil-react-components/layout';
import { Label, Text } from '@amzn/stencil-react-components/text';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  buildTemplateListOption,
  templateListFilter,
  validateEmail,
} from '../../util/campaign/campaignUtils';
import {
  CampaignFormSection,
  TemplateType,
  TEMPLATE_FORM_CARDS,
} from '../../enums/schedulingConstant';
import { updateCampaignForm } from '../../actions/createScheduleAction';
import { CardWithRadio } from '../atoms/CardWithRadio';
import TemplatePreview from '../molecules/TemplatePreview';
import { CREATE_CONTENT_PAGE } from '../../constants/routesConstant';
import { useHistory } from 'react-router-dom';
import { Button } from '@amzn/stencil-react-components/button';
import SendTestCampaign from './SendTestCampaign';
import { FROM_ADDRESS_FOOTER_ERROR_TEXT, FROM_ADDRESS_FOOTER_TEXT } from '../../constants/messages';

export const TemplateSection = props => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const history = useHistory();
  const templateFormDetails = useSelector(
    store => store.campaign.campaignFormState.templateDetails,
  );
  const [selectedTemplate = '', setSelectedTemplate] = useState(
    templateFormDetails.messageTemplateName,
  );
  const templateList = useSelector(store => store.nudge.messageTemplateList);
  const buildTemplateList = buildTemplateListOption(templateList);
  const templateRenderOption = option => (
    <Row display="flex" flexDirection="column" gridGap="S200">
      <Row alignItems="center">{option?.name}</Row>
      {option?.createdBy && (
        <View display={'flex'} gridGap="S200" flexDirection="row">
          <Text fontSize="T100">Created by: </Text>
          <Spacer width={'7px'} />
          <Text fontSize="T100" style={{ fontWeight: 'bold' }}>
            {option?.createdBy}
          </Text>
        </View>
      )}
    </Row>
  );
  const templateFilterOption = searchText => templateListFilter(searchText, buildTemplateList);

  const messageTemplateList = templateList?.reduce((acc, cur) => {
    acc.push(cur.messageTemplateName);
    return acc;
  }, []);

  const [templateType, setTemplateType] = useState(TemplateType.EXISTING);
  const [isPreview, setIsPreview] = useState();
  const isSubmitSuccess = useSelector(store => store.campaign.campaignFormState.isSubmitSuccess);
  const senderAddress = useSelector(
    store => store.campaign.campaignFormState.templateDetails.fromAddress,
  );
  const [fromAddress, setFromAddress] = useState(senderAddress);
  const [senderAddressFooterText, setSenderAddressFooterText] = useState(FROM_ADDRESS_FOOTER_TEXT);
  const [hasEmailError, setHasEmailError] = useState();

  const onTemplateSelect = async e => {
    setSelectedTemplate(e);
    setIsPreview(false);
    await dispatch(
      updateCampaignForm(CampaignFormSection.TEMPLATE, {
        messageTemplateName: e,
      }),
    );
    setIsPreview(true);
  };

  const onSenderAddressChange = async e => {
    setFromAddress(e.target.value);
    if (validateEmail(e.target.value)) {
      setHasEmailError(false);
      setSenderAddressFooterText(FROM_ADDRESS_FOOTER_TEXT);
      await dispatch(
        updateCampaignForm(CampaignFormSection.TEMPLATE, {
          fromAddress: e.target.value ?? '',
        }),
      );
    } else {
      setHasEmailError(true);
      setSenderAddressFooterText(FROM_ADDRESS_FOOTER_ERROR_TEXT);
      await dispatch(
        updateCampaignForm(CampaignFormSection.TEMPLATE, {
          fromAddress: '',
        }),
      );
      return;
    }
  };

  const cardList = TEMPLATE_FORM_CARDS;
  return (
    <Row display="flex" flexDirection="column" gridGap="S400">
      <Col display="flex" flexDirection="row" gridGap="S200">
        {cardList.map((card, index) => {
          return (
            <>
              <CardWithRadio
                width={card.width}
                label={card.label}
                description={card.description}
                radioName={card.radioName}
                radioValue={card.radioValue}
                onChange={() => setTemplateType(card.radioValue)}
                isDisabled={card.isDisabled || isSubmitSuccess}
                isChecked={card.radioValue === templateType}
                key={index}
              />
              <Spacer width={30} />
            </>
          );
        })}
      </Col>
      {templateType === TemplateType.EXISTING ? (
        <>
          <Spacer height={20} />
          <Label htmlFor="some-select" id="choose-template-label">
            <FormattedMessage
              id="app.campaign.label.chooseATemplate"
              defaultMessage="Choose a message template"
            />
          </Label>
          <Select
            error={false}
            id="select-template"
            data-testid="select-template-dp"
            isMulti={false}
            isSearchable={true}
            labelId="choose-template-label"
            value={selectedTemplate}
            disabled={isSubmitSuccess}
            onChange={e => {
              onTemplateSelect(e);
            }}
            options={messageTemplateList}
            valueAccessor={templateRenderOption}
            renderOption={templateRenderOption}
            filterOptions={templateFilterOption}
            noOptionsText="No options found."
            placeholder="Select a message template for campaign"
          />
          <Spacer height={25} />
          <Col />
        </>
      ) : (
        <>
          <View>
            <Button
              data-testid="campaign-cancel-btn"
              onClick={() => history.push(CREATE_CONTENT_PAGE)}
              tertiary={true}
            >
              <FormattedMessage id="app.button.createContent" defaultMessage="Create Content" />
            </Button>
          </View>
        </>
      )}
      {templateFormDetails?.messageTemplateName?.name && (
        <>
          <Label>Preview Link:</Label>
          <TemplatePreview templateName={templateFormDetails?.messageTemplateName} />
        </>
      )}
      <Col display="flex" gridGap="S200">
        <Text fontSize={'T400'} color="primaryLabelText">
          <FormattedMessage
            id="app.campaign.template.label.emailSetting"
            defaultMessage="Email Settings"
          />
        </Text>
        <InputWrapper
          labelText=""
          fontSize={'T400'}
          type="email"
          id="sender-email-wrapper"
          placeholder={formatMessage({ id: 'app.campaign.template.label.emailSetting' })}
          error={hasEmailError}
          footer={senderAddressFooterText}
        >
          {inputProps => (
            <Input
              {...inputProps}
              data-testid="sender-email-input"
              error={hasEmailError}
              type="email"
              value={fromAddress}
              disabled={isSubmitSuccess}
              onChange={e => onSenderAddressChange(e)}
            />
          )}
        </InputWrapper>
      </Col>

      <Col display="flex" gridGap="S200">
        <SendTestCampaign />
      </Col>
    </Row>
  );
};
